import { ArrowUp, ArrowDown, Minus, FileText, Database, Quote } from "lucide-react";
import ConcentrationChart from "../ConcentrationChart";
import { RiskBadge, CAT_COLOR } from "../RiskBadge";
import { fmtLabel, orderRows } from "../../lib/sourceLinks";

function TrendInline({ trend }) {
  if (!trend || trend.dir === "flat") return <span className="inline-flex items-center gap-1 text-sm text-neutral-400"><Minus className="h-4 w-4" /> Flat</span>;
  const map = { rising: { c: "#E24B4A", Icon: ArrowUp }, easing: { c: "#1D9E75", Icon: ArrowDown } };
  const { c, Icon } = map[trend.dir];
  return <span className="inline-flex items-center gap-1 text-sm font-medium" style={{ color: c }}><Icon className="h-4 w-4" /> {trend.label}</span>;
}

// A clean two-column breakdown table (label + %). Used for customer parties, product lines, etc.
// Rows with `pct_is_floor: true` render as "≥X%" — the filing only disclosed a minimum threshold
// (e.g. "each customer contributed more than 10%"), not an exact percentage.
function BreakdownTable({ title, rows, footer, testid }) {
  if (!rows || !rows.length) return null;
  return (
    <div className="mt-3" data-testid={testid}>
      {title && <div className="mb-1 text-xs uppercase tracking-wider text-neutral-400">{title}</div>}
      <div className="overflow-hidden rounded-md border border-[#e8e6df]">
        <table className="w-full text-sm">
          <tbody>
            {rows.map((r, i) => (
              <tr key={`${r.name}-${r.pct}-${i}`} data-testid={`${testid}-row-${i}`} className="border-b border-[#f0eee6] align-top last:border-0">
                <td className="px-3 py-2 text-neutral-700">{r.name}</td>
                <td className="whitespace-nowrap px-3 py-2 text-right font-mono font-semibold text-[#1a1a18]">
                  {r.pct_is_floor ? "\u2265" : ""}{r.pct}%
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {footer && <div className="mt-1 text-xs text-neutral-400">{footer}</div>}
    </div>
  );
}

function QualTimeline({ history }) {
  const sq = { high: "#E24B4A", medium: "#E0922F", low: "#1D9E75" };
  return (
    <div className="flex h-48 w-full flex-col justify-center">
      <div className="flex items-end gap-2">
        {history.map((h) => (
          <div key={h.year} className="flex flex-1 flex-col items-center gap-2">
            <div className="w-full rounded-sm" style={{ height: h.level === "high" ? 90 : h.level === "medium" ? 55 : 25, backgroundColor: sq[h.level] }} />
            <span className="text-[10px] text-neutral-400">{h.year}</span>
          </div>
        ))}
      </div>
      <p className="mt-3 text-xs text-neutral-400">Qualitative dependency level by filing year.</p>
    </div>
  );
}

// A headline like "Top 10 Customers", "Five Largest Customers", "Three Distributors" or
// "Principal US Customers" is a GROUP aggregate — its members are a SUBSET of that total,
// so they must not be added to it. Infer group from the label even when is_group is unset.
const GROUP_RE = /\b(top\s*\d+|two|three|four|five|six|seven|eight|nine|ten|several|few|\d+)\b[^.]*\b(customers|clients|distributors|resellers|wholesalers|members)\b|\b(largest|major|significant|key|principal)\b[^.]*\b(customers|clients|distributors)\b/i;

// Breakdown entries from tagged/named parties. For a GROUP headline these are members
// (a SUBSET of the combined total). For INDIVIDUAL customers each entry is a SEPARATE
// peer customer — never additive with, nor a sub-part of, the headline customer.
function customerRows(dim) {
  const isGroup = !!dim.is_group || GROUP_RE.test(dim.top_entity || "");
  const rawBreakdown = (Array.isArray(dim.party_breakdown) && dim.party_breakdown.length)
    ? dim.party_breakdown : [];
  // Preserve the disclosed designation (Customer A / Distributor B / Cardinal Health);
  // only synthesize "Customer N" when the label is genuinely missing.
  const labelOf = (n, i) => (n ? fmtLabel(n) : `Customer ${i + 1}`);
  const groupRows = isGroup
    ? orderRows(rawBreakdown.filter((b) => b.name !== dim.top_entity)
        .sort((a, b) => (b.pct || 0) - (a.pct || 0))
        .map((p, i) => ({ name: labelOf(p.name, i), pct: p.pct, pct_is_floor: !!p.pct_is_floor })))
    : [];
  // INDIVIDUAL peers: other distinct customers, dropping any entry that merely restates
  // the headline customer (same % + same/blank label).
  const peerRows = !isGroup
    ? orderRows(rawBreakdown
        .filter((b) => b.name !== dim.top_entity &&
          !(Math.abs((b.pct || 0) - (dim.top_pct || 0)) < 0.05 &&
            (b.name == null || fmtLabel(b.name) === fmtLabel(dim.top_entity || ""))))
        .sort((a, b) => (b.pct || 0) - (a.pct || 0))
        .map((p, i) => ({ name: labelOf(p.name, i), pct: p.pct, pct_is_floor: !!p.pct_is_floor })))
    : [];
  return { isGroup, groupRows, peerRows };
}

function CustomerBreakdowns({ dim, isGroup, groupRows, peerRows }) {
  const hasNames = groupRows.some((b) => b.name && !/^Customer \d+$/.test(b.name));
  return (
    <>
      {dim.per_segment && Array.isArray(dim.segment_breakdown) && (
        <BreakdownTable
          title="Top customers by reportable segment"
          testid="segment-breakdown"
          rows={orderRows(dim.segment_breakdown).map((s) => ({ name: `${s.name} — ${s.count} customers`, pct: s.pct }))}
          footer="Each figure is the top customers' share of that segment's net sales — measured per-segment, so the figures are not additive"
        />
      )}
      {!dim.per_segment && isGroup && groupRows.length > 0 && (
        <BreakdownTable
          testid="customer-parties"
          rows={groupRows}
          footer={(() => {
            const allFloor = groupRows.every((b) => b.pct_is_floor);
            if (allFloor) {
              return `Each member exceeded the ${groupRows[0].pct}% disclosure threshold — the filing does not quantify the individual splits.`;
            }
            const sum = +groupRows.reduce((a, b) => a + (b.pct || 0), 0).toFixed(1);
            const cnt = dim.customer_count;
            if (sum > 101)
              return "Members are tagged against different revenue bases — not additive";
            if (cnt && groupRows.length < cnt)
              return `${groupRows.length} of ${cnt} members disclosed · ${sum}% of the ${dim.top_pct}% combined group`;
            return hasNames ? "Members of this group" : "Members of the combined group";
          })()}
        />
      )}
      {!dim.per_segment && !isGroup && peerRows.length > 0 && (
        <BreakdownTable
          title={dim.subset_breakdown ? "Within this total" : "Other major customers"}
          testid="customer-parties"
          rows={peerRows}
          footer={dim.subset_breakdown
            ? "Component(s) of the headline figure, as broken out in the filing — a subset, not an addition."
            : "Each is a separate customer disclosed individually — the figures are not sub-parts of the headline and may be measured against different revenue bases, so they are not additive."}
        />
      )}
    </>
  );
}

function BasisNote({ dim }) {
  if (!dim.basis_inconsistent) return null;
  return (
    <div data-testid="basis-inconsistent-note" className="mt-2 rounded-md border border-[#E0922F]/40 bg-[#FBF3E6] px-3 py-2 text-xs leading-relaxed text-[#8a5a12]">
      <span className="font-semibold">Interpret with caution.</span>{" "}
      {dim.basis_note ? (
        <>The disclosed customer shares sum to more than 100% and are not additive.
        The filing explains why:</>
      ) : (
        <>The filing tags several
        customer concentrations whose disclosed shares sum to more than 100%, so they either
        overlap (one is a subset of another — e.g. an agency within a government total) or are
        measured against different revenue bases. Either way they are not additive, and no
        single company-wide top-customer share can be derived. The headline is the largest
        single figure as tagged.</>
      )}
      {dim.basis_note && (
        <p data-testid="basis-note" className="mt-1.5 border-l-2 border-[#E0922F]/50 pl-2 italic">
          "{dim.basis_note}"
        </p>
      )}
    </div>
  );
}

export default function DimensionCard({ d, dim, onViewSource }) {
  const color = CAT_COLOR[d.key];
  const { isGroup, groupRows, peerRows } = customerRows(dim);
  const canOpen = !dim.absent && dim.top_pct != null && dim.top_pct !== 0;
  return (
    <div data-testid={`dimension-${d.key}`} className="rounded-lg border border-[#e8e6df] bg-white p-6 md:p-8">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
            <h2 className="font-heading text-2xl font-semibold text-[#1a1a18]">{d.label}</h2>
          </div>
          <p className="mt-1 text-sm text-neutral-500">{d.note}</p>
        </div>
        <RiskBadge level={dim.level} />
      </div>

      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        <div>
          <div className="text-xs uppercase tracking-wider text-neutral-400">
            {d.key === "credit" ? "Receivables" : d.type === "qual" ? "Dependency" : "Top exposure"}
          </div>
          {d.type === "qual" && !dim.quantified ? (
            <div data-testid={`headline-${d.key}`} className="mt-1 break-words font-heading text-xl font-bold leading-tight text-[#1a1a18]">
              {dim.label}
            </div>
          ) : (
            <div data-testid={`headline-${d.key}`} className="mt-1 font-heading text-4xl font-bold text-[#1a1a18]">
              {dim.absent && d.type !== "qual"
                ? "<10%"
                : `${dim.pct_is_floor ? "\u2265" : ""}${dim.top_pct}%`}
            </div>
          )}
          <div className="mt-1 break-words text-sm text-neutral-600">
            {fmtLabel(dim.top_entity)}
            {dim.is_named && !/^(top |largest of )/i.test(dim.top_entity || "") && <span className="ml-2 rounded bg-neutral-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-neutral-600">Named</span>}
          </div>
          {dim.basis && !dim.absent && (
            <div data-testid={`basis-note-${d.key}`} className="mt-2 text-xs text-neutral-500">
              Measured against <span className="font-semibold">{dim.basis}</span>
              {dim.basis_share
                ? <> — which the filing states are <span className="font-semibold">{dim.basis_share}% of total revenues</span>, so ≈<span className="font-semibold">{Math.round(dim.top_pct * dim.basis_share / 100)}%</span> of the company's total.</>
                : <> — the basis stated in the filing, not necessarily total revenue.</>}
            </div>
          )}
          {dim.pct_is_floor && !dim.absent && (
            <div data-testid={`floor-note-${d.key}`} className="mt-2 text-xs text-neutral-500">
              {(dim.party_breakdown || []).length > 1 ? (
                <>The filing discloses a <span className="font-semibold">minimum threshold</span> — each is at least {dim.top_pct}% but the exact individual amounts are not quantified.</>
              ) : (
                <>The filing discloses a <span className="font-semibold">minimum threshold</span> — at least {dim.top_pct}%; the exact figure is not quantified.</>
              )}
            </div>
          )}
          {d.key === "customer" && <BasisNote dim={dim} />}
          {d.key === "supplier" && !dim.absent && !dim.quantified && (
            <div className="mt-1.5 text-xs text-neutral-400" data-testid="supplier-informational">
              Qualitative disclosure — no % quantified (informational, not scored)
            </div>
          )}
          {d.key === "customer" && (
            <CustomerBreakdowns dim={dim} isGroup={isGroup} groupRows={groupRows} peerRows={peerRows} />
          )}
          {d.key === "product" && Array.isArray(dim.products) && dim.products.length > 1 && (
            <BreakdownTable
              title="Products disclosed"
              testid="product-list"
              rows={orderRows(dim.products)}
              footer={dim.products.reduce((a, b) => a + (b.pct || 0), 0) > 100
                ? "Figures measure overlapping categories (e.g. franchise vs. platform vs. single product) and are not additive"
                : null}
            />
          )}
          <div className="mt-3"><TrendInline trend={dim.trend} /></div>
          {canOpen && (
            <p className="mt-4 text-xs text-neutral-400">Click a point to open the SEC source</p>
          )}
        </div>

        <div className="md:col-span-2">
          {d.type === "qual" ? (
            <QualTimeline history={dim.history} />
          ) : (
            <ConcentrationChart history={dim.history} color={color} label={d.label}
              onPointClick={canOpen ? onViewSource : undefined} />
          )}
        </div>
      </div>

      <button type="button" onClick={onViewSource}
        data-testid={`view-source-${d.key}`}
        className="mt-6 block w-full rounded-md border border-[#e8e6df] bg-[#f5f4ef] p-4 text-left transition-colors hover:border-[#cfae6b] hover:bg-[#f0eee6]">
        <div className="mb-2 flex items-center justify-between gap-2">
          <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-neutral-500">
            <FileText className="h-3.5 w-3.5" /> Disclosure language · {dim.form_type}
          </span>
          <span className="inline-flex items-center gap-1 text-xs font-medium text-[#8a6d2f]">
            {dim.source === "xbrl" ? <Database className="h-3.5 w-3.5" /> : <Quote className="h-3.5 w-3.5" />}
            View source
          </span>
        </div>
        {d.key === "product" && Array.isArray(dim.products) && dim.products.length > 1 ? (
          <div className="space-y-2" data-testid="raw-sentence-product">
            {orderRows(dim.products).map((p) => (
              <div key={`${p.name}-${p.pct}`} className="border-l-2 border-[#cfae6b] pl-3">
                <div className="text-xs font-semibold text-neutral-500">{p.name} · {p.pct}%</div>
                <p className="font-mono text-sm leading-relaxed text-neutral-700">"{p.raw_sentence}"</p>
              </div>
            ))}
          </div>
        ) : (
          <p data-testid={`raw-sentence-${d.key}`} className="font-mono text-sm leading-relaxed text-neutral-700">"{dim.raw_sentence}"</p>
        )}
      </button>
    </div>
  );
}
